import { CreateConfigurationInput, JsonObject } from '../domain/types';
import { parseJsonObject } from './json.utils';

/**
 * The only top-level keys a pasted configuration may carry: exactly
 * `CreateConfigurationInput`, which is also the shape of the reviewed files in
 * `partner-data-gateway-service/configurations/<dataProvider>/<name>.json`.
 */
export const PASTE_ALLOWED_KEYS = [
  'name',
  'dataProvider',
  'definition',
  'actions',
] as const;

export const PASTE_EMPTY_MESSAGE = 'Paste a configuration JSON object.';
export const PASTE_NAME_MESSAGE = '`name` must be a non-empty string.';
export const PASTE_DATA_PROVIDER_MESSAGE =
  '`dataProvider` must be a non-empty string.';
export const PASTE_DATA_PROVIDER_SLASH_MESSAGE =
  '`dataProvider` must not contain "/": PDGS looks configurations up by the provider segment of the S3 key, so it would never match.';
export const PASTE_DEFINITION_MESSAGE = '`definition` must be a JSON object.';
export const PASTE_ACTIONS_MESSAGE =
  '`actions` must be a non-empty array of JSON objects.';

export const pasteUnknownKeysMessage = (keys: string[]): string =>
  `Unexpected top-level ${keys.length === 1 ? 'key' : 'keys'}: ${keys
    .map(key => `\`${key}\``)
    .join(', ')}. Only ${PASTE_ALLOWED_KEYS.map(key => `\`${key}\``).join(
    ', ',
  )} are accepted. A configuration copied from an environment (with \`id\`, \`status\`, timestamps) can't be pasted as-is.`;

export interface ConfigurationPasteResult {
  /** The payload to send, or `null` when `errors` is non-empty. */
  value: CreateConfigurationInput | null;
  /** Every problem found, in a stable order, so all are fixed in one pass. */
  errors: string[];
}

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Parses and checks a pasted configuration before it is sent to PDGS.
 *
 * Deliberately shallow: PDGS owns the deep contract (criteria keys, action
 * metadata, DOB formats...) and reports it on create. This only catches what
 * would otherwise turn into a confusing round trip, and never rewrites the
 * payload: `definition` and `actions` go out exactly as pasted, so what was
 * reviewed in the PR is what PDGS receives. `name` and `dataProvider` are
 * trimmed, matching the form.
 */
export const parseConfigurationPaste = (
  text: string,
): ConfigurationPasteResult => {
  if (text.trim() === '') {
    return { value: null, errors: [PASTE_EMPTY_MESSAGE] };
  }

  const parsed = parseJsonObject(text);

  if (!parsed.value) {
    return { value: null, errors: [parsed.error ?? PASTE_EMPTY_MESSAGE] };
  }

  const candidate = parsed.value;
  const errors: string[] = [];

  const unknownKeys = Object.keys(candidate).filter(
    key => !(PASTE_ALLOWED_KEYS as readonly string[]).includes(key),
  );

  if (unknownKeys.length > 0) {
    errors.push(pasteUnknownKeysMessage(unknownKeys));
  }

  const { name, dataProvider, definition, actions } = candidate;

  if (!isNonEmptyString(name)) {
    errors.push(PASTE_NAME_MESSAGE);
  }

  if (!isNonEmptyString(dataProvider)) {
    errors.push(PASTE_DATA_PROVIDER_MESSAGE);
  } else if (dataProvider.includes('/')) {
    errors.push(PASTE_DATA_PROVIDER_SLASH_MESSAGE);
  }

  if (!isJsonObject(definition)) {
    errors.push(PASTE_DEFINITION_MESSAGE);
  }

  const actionsValid =
    Array.isArray(actions) && actions.length > 0 && actions.every(isJsonObject);

  if (!actionsValid) {
    errors.push(PASTE_ACTIONS_MESSAGE);
  }

  if (
    errors.length > 0 ||
    !isNonEmptyString(name) ||
    !isNonEmptyString(dataProvider) ||
    !isJsonObject(definition) ||
    !actionsValid
  ) {
    return { value: null, errors };
  }

  return {
    value: {
      name: name.trim(),
      dataProvider: dataProvider.trim(),
      definition,
      actions: actions as JsonObject[],
    },
    errors: [],
  };
};

/**
 * A stored configuration as the file shape the paste and edit editors use:
 * exactly the `CreateConfigurationInput` keys, in a stable order, so a row
 * copied out of ops-hub can be committed to `configurations/` as-is.
 */
export const toConfigurationFile = (
  configuration: CreateConfigurationInput,
): CreateConfigurationInput => ({
  name: configuration.name,
  dataProvider: configuration.dataProvider,
  definition: configuration.definition,
  actions: configuration.actions,
});

/** JSON with object keys sorted, so key order never reads as a change. */
const canonicalJson = (value: unknown): string =>
  JSON.stringify(value, (_key, inner: unknown) =>
    typeof inner === 'object' && inner !== null && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner as JsonObject).sort(([a], [b]) =>
            a.localeCompare(b),
          ),
        )
      : inner,
  );

/**
 * The top-level fields that differ between two configurations, in
 * `PASTE_ALLOWED_KEYS` order. Compared by value, ignoring object key order and
 * whitespace, so re-formatting the JSON is not a change.
 */
export const changedConfigurationFields = (
  current: CreateConfigurationInput,
  next: CreateConfigurationInput,
): (keyof CreateConfigurationInput)[] =>
  PASTE_ALLOWED_KEYS.filter(
    key => canonicalJson(current[key]) !== canonicalJson(next[key]),
  );
